import { TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  allowedIndexTypesForFieldType,
  effectiveIndexTypesForField,
  SQL_SCHEMA_FIELD_TYPES,
  type SchemaFieldIndexType,
  type SchemaFieldType,
} from '@erp/shared-types';
import { DefaultValueInput, initialDefaultForType } from './DefaultValueInput';
import type {
  FriendlyFieldRow,
  FriendlyFieldType,
} from './quickSchemaFieldTree';

/** Sensible starting precision/scale when a row is first switched to "numeric" — the user adjusts from here. */
const DEFAULT_NUMERIC_ARGS = { precision: 10, scale: 2 };

// Untranslated: these are Postgres index access-method names, not prose.
const INDEX_TYPE_LABELS: Record<SchemaFieldIndexType, string> = {
  btree: 'B-tree',
  hash: 'Hash',
  trigram: 'Trigram',
  gin: 'GIN',
};

type SqlSchemaFieldRowProps = {
  row: FriendlyFieldRow;
  formId: string;
  saving: boolean;
  /** Whether this field is part of the schema's composite key. Only meaningful when `onKeyCheck` is given. */
  isKey?: boolean;
  onFieldNameChange: (rowId: string, oldName: string, newName: string) => void;
  onUpdate: (
    rowId: string,
    updater: (row: FriendlyFieldRow) => FriendlyFieldRow,
  ) => void;
  onRemove?: (rowId: string) => void;
  /** Presence enables the Key checkbox (only the "create schema" dialog manages a key this way). */
  onKeyCheck?: (rowId: string, checked: boolean) => void;
  /** Header Trash button. Off when editing a single existing field (the caller owns removal via Cancel/Remove). */
  showActions?: boolean;
};

/**
 * Field editor for `schemaType: "sql"` schemas — a flat, minimal vocabulary
 * (string/integer/numeric/date, see `SQL_SCHEMA_FIELD_TYPES`) that's 1:1
 * mappable to Postgres columns. Deliberately does not reuse
 * `QuickSchemaFieldRow`: that component's options (enum, object/array
 * nesting, extraction hints) describe Extend AI extraction targets, none of
 * which apply here, and it has no inputs for a "numeric" field's
 * precision/scale.
 */
export function SqlSchemaFieldRow({
  row,
  formId,
  saving,
  isKey = false,
  onFieldNameChange,
  onUpdate,
  onRemove,
  onKeyCheck,
  showActions = true,
}: SqlSchemaFieldRowProps) {
  const gt = useGT();
  const isNumeric = row.fieldType === 'numeric';

  const rowFieldType = row.fieldType as SchemaFieldType;
  const allowedIndexTypes = allowedIndexTypesForFieldType(rowFieldType);
  // Indexes are opt-in: a field is indexed only for the kinds it explicitly
  // declares. A null/unset config means no indexes (nothing checked).
  // Toggling any checkbox pins the row to an explicit config.
  const effectiveIndexTypes = new Set(
    effectiveIndexTypesForField({
      fieldType: rowFieldType,
      indexes: row.indexes ?? null,
    }),
  );

  function toggleIndexType(kind: SchemaFieldIndexType, checked: boolean) {
    onUpdate(row.id, (current) => {
      const currentKinds = new Set(
        effectiveIndexTypesForField({
          fieldType: current.fieldType as SchemaFieldType,
          indexes: current.indexes ?? null,
        }),
      );
      if (checked) {
        currentKinds.add(kind);
      } else {
        currentKinds.delete(kind);
      }
      // Keep a stable, allowed-list order for the persisted config.
      const next = allowedIndexTypesForFieldType(
        current.fieldType as SchemaFieldType,
      ).filter((candidate) => currentKinds.has(candidate));
      return { ...current, indexes: next };
    });
  }

  const requiredControl = (
    <div className="flex items-center gap-2 pb-1">
      <Checkbox
        id={`${formId}-req-${row.id}`}
        checked={row.isRequired}
        onCheckedChange={(checked) =>
          onUpdate(row.id, (current) =>
            isKey ? current : { ...current, isRequired: checked === true },
          )
        }
        disabled={saving || isKey}
      />
      <Label
        htmlFor={`${formId}-req-${row.id}`}
        className="text-xs font-normal"
      >
        <T context="required checkbox sql schema field">Required</T>
      </Label>
    </div>
  );

  const hasDefaultCheckbox = (
    <div className="flex items-center gap-2 pb-1">
      <Checkbox
        id={`${formId}-hasdefault-${row.id}`}
        checked={row.hasDefault}
        onCheckedChange={(checked) =>
          onUpdate(row.id, (current) => ({
            ...current,
            hasDefault: checked === true,
            defaultValue:
              checked === true
                ? (current.defaultValue ??
                  initialDefaultForType(current.fieldType))
                : null,
          }))
        }
        disabled={saving}
      />
      <Label
        htmlFor={`${formId}-hasdefault-${row.id}`}
        className="whitespace-nowrap text-xs font-normal"
      >
        <T context="checkbox to enable a column default value sql schema field">
          Has default
        </T>
      </Label>
    </div>
  );

  return (
    <div className="rounded-md border bg-card p-2">
      {showActions && onRemove ? (
        <div className="mb-2 flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label={gt('Remove field', {
              context: 'aria remove sql schema field',
            })}
            onClick={() => onRemove(row.id)}
            disabled={saving}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[7rem] flex-1 space-y-1">
          <Label className="text-[10px] text-muted-foreground">
            <T context="column field name sql schema field">Field</T>
          </Label>
          <Input
            value={row.fieldName}
            onChange={(e) =>
              onFieldNameChange(row.id, row.fieldName, e.target.value)
            }
            placeholder={gt('item_no', {
              context: 'placeholder field key sql schema field',
            })}
            className="h-8 font-mono text-xs"
            disabled={saving}
          />
        </div>

        <div className="w-[7.5rem] space-y-1">
          <Label className="text-[10px] text-muted-foreground">
            <T context="column type sql schema field">Type</T>
          </Label>
          <Select
            value={row.fieldType}
            onValueChange={(v) =>
              onUpdate(row.id, (current) => ({
                ...current,
                fieldType: v as FriendlyFieldType,
                hasDefault: false,
                defaultValue: null,
                validationRules:
                  v === 'numeric'
                    ? (current.validationRules ?? DEFAULT_NUMERIC_ARGS)
                    : null,
                // A different column type allows different index kinds — fall
                // back to the new type's default rather than carrying over a
                // possibly-incompatible explicit config.
                indexes: null,
              }))
            }
            disabled={saving}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SQL_SCHEMA_FIELD_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isNumeric ? (
          <>
            <div className="w-20 space-y-1">
              <Label className="text-[10px] text-muted-foreground">
                <T context="numeric precision input sql schema field">
                  Precision
                </T>
              </Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={row.validationRules?.precision ?? ''}
                onChange={(e) =>
                  onUpdate(row.id, (current) => ({
                    ...current,
                    validationRules: {
                      ...current.validationRules,
                      precision:
                        e.target.value === ''
                          ? undefined
                          : Number(e.target.value),
                    },
                  }))
                }
                className="h-8 text-xs"
                disabled={saving}
              />
            </div>
            <div className="w-20 space-y-1">
              <Label className="text-[10px] text-muted-foreground">
                <T context="numeric scale input sql schema field">Scale</T>
              </Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={row.validationRules?.scale ?? ''}
                onChange={(e) =>
                  onUpdate(row.id, (current) => ({
                    ...current,
                    validationRules: {
                      ...current.validationRules,
                      scale:
                        e.target.value === ''
                          ? undefined
                          : Number(e.target.value),
                    },
                  }))
                }
                className="h-8 text-xs"
                disabled={saving}
              />
            </div>
          </>
        ) : null}

        {isKey ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{requiredControl}</TooltipTrigger>
              <TooltipContent>
                <T context="tooltip explaining required is locked for key fields sql schema field">
                  Key fields are always required.
                </T>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          requiredControl
        )}

        {onKeyCheck ? (
          <div className="flex items-center gap-2 pb-1">
            <Checkbox
              id={`${formId}-key-${row.id}`}
              checked={isKey}
              onCheckedChange={(checked) =>
                onKeyCheck(row.id, checked === true)
              }
              disabled={saving}
            />
            <Label
              htmlFor={`${formId}-key-${row.id}`}
              className="text-xs font-normal"
            >
              <T context="key checkbox sql schema field">Key</T>
            </Label>
          </div>
        ) : null}

        <div className="w-full space-y-1">
          <Label className="text-[10px] text-muted-foreground">
            <T context="default value label sql schema field">Default Value</T>
          </Label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <DefaultValueInput
                key={`${row.fieldType}-${row.hasDefault}`}
                fieldType={row.fieldType}
                value={row.defaultValue}
                onChange={(next) =>
                  onUpdate(row.id, (current) => ({
                    ...current,
                    defaultValue: next,
                  }))
                }
                disabled={saving || !row.hasDefault}
                placeholder={
                  row.hasDefault
                    ? undefined
                    : gt('No default value', {
                        context:
                          'placeholder when default value is disabled sql schema field',
                      })
                }
                compact
              />
            </div>
            {hasDefaultCheckbox}
          </div>
        </div>

        <div className="w-full space-y-1">
          <Label className="text-[10px] text-muted-foreground">
            <T context="index types label sql schema field">Indexes</T>
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            {allowedIndexTypes.map((kind) => (
              <div key={kind} className="flex items-center gap-2">
                <Checkbox
                  id={`${formId}-index-${kind}-${row.id}`}
                  checked={effectiveIndexTypes.has(kind)}
                  onCheckedChange={(checked) =>
                    toggleIndexType(kind, checked === true)
                  }
                  disabled={saving}
                />
                <Label
                  htmlFor={`${formId}-index-${kind}-${row.id}`}
                  className="text-xs font-normal"
                >
                  {INDEX_TYPE_LABELS[kind]}
                </Label>
              </div>
            ))}
            {effectiveIndexTypes.size === 0 ? (
              <span className="text-[10px] text-muted-foreground">
                <T context="hint that no indexes are configured for this sql schema field">
                  (no indexes)
                </T>
              </span>
            ) : null}
          </div>
        </div>

        <div className="w-full space-y-1">
          <Label className="text-[10px] text-muted-foreground">
            <T context="column description sql schema field">Description</T>
          </Label>
          <Input
            value={row.description}
            onChange={(e) =>
              onUpdate(row.id, (current) => ({
                ...current,
                description: e.target.value,
              }))
            }
            placeholder={gt('Optional', {
              context: 'placeholder field description sql schema field',
            })}
            className="h-8 text-xs"
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
}
